import { View } from "extensions/marionette";
import { _ } from "extensions/underscore";
import Radio from "backbone.radio";
import $ from "jquery";
import sweetalert from "sweetalert";
import { toast } from "extensions/toast";

import { store } from "common/store";
import strings from "common/strings";
import { authentication } from "common/config/api";
import { destroyPopovers } from "common/utils";
import { DO_MIMIC_USER } from "common/actions/authentication";

import { request } from "common/components/request";
import { UserModel } from "common/features/user/model";
import { UsersTable } from "common/features/user/usersTable";

import { references } from "common/helpers/references";
import { SmartTableModel } from "common/components/table/smartTableModel";

import userTemplate from "merchant/features/userManagement/users/userTemplate.jst";

import "merchant/features/userManagement/users/users.scss";

export const userManagementMixin = {
    name: strings.userManagement,
    className: "users",
    childViewEvents: {
        toggleUserActivation: "toggleUserActivation",
        mimicUser: "mimicUser",
        editUser: "editExistingUser",
        unlockUser: "unlockUser",
        resetPassword: "resetPassword",
        sendActivation: "sendActivation"
    },
    
    unlockUser(model) {
        sweetalert({
            title: strings.confirmUnlockUser,
            content: $(`<blockquote class='remove-user'>${userTemplate(model.toJstJSON())}</blockquote>`)[0],
            icon: "warning",
            buttons: {
                confirm: {
                    text: strings.unlockUser,
                    className: "btn-confirm"
                },
                cancel: {
                    visible: true
                }
            }
        }).then((confirmed) => {
            if (confirmed) {
                model.set({
                    locked: false
                });
                
                request(authentication.unlock, {
                    data: { id: model.id },
                    method: "POST"
                }).then(() => {
                    const { first_name, last_name } = model.toJSON();
                    toast.success(`${first_name} ${last_name} has been unlocked`);
                }, () => {
                    // Revert back model state
                    model.set({
                        locked: true
                    });
                    toast.error("There was an error unlocking a user");
                }).finally(() => {
                    destroyPopovers();
                });
            }
        });
    },
    
    showThatsYou(model) {
        sweetalert({
            title: strings.cannotSelectYourself,
            content: $(`<blockquote class='remove-user'>${userTemplate(model.toJstJSON())}</blockquote>`)[0],
            icon: "warning",
            buttons: {
                confirm: {
                    text: strings.gotIt,
                    className: "btn-confirm"
                }
            }
        });
        return this;
    },
    
    showThatsSsoUser(model) {
        sweetalert({
            title: strings.cannotDeactivateSsoUser,
            content: $(`<blockquote class='remove-user'>${userTemplate(model.toJstJSON())}</blockquote>`)[0],
            icon: "warning",
            buttons: {
                confirm: {
                    text: strings.gotIt,
                    className: "btn-confirm"
                }
            }
        });
        return this;
    },
    
    toggleUserActivation({ model }) {
        if (model.isSsoUser() && !store.getState().permissions.user_permissions.can_deactivate_sso_users) {
            this.showThatsSsoUser(model);
        } else if (this.isCurrentActiveUser(model)) {
            this.showThatsYou(model);
        } else {
            // Toggle the user
            let url;
            let nextActiveState;
            if (model.get("active") === true) {
                url = authentication.deactivate;
                nextActiveState = false;
            } else {
                url = authentication.activate;
                nextActiveState = true;
            }
    
            model.set({
                active: nextActiveState
            });
            
            request(url, {
                data: { id: model.id },
                method: "POST"
            }).then(() => {
                const { first_name, last_name } = model.toJSON();
                if (nextActiveState) {
                    toast.success(`${first_name} ${last_name} has been activated`);
                } else {
                    toast.success(`${first_name} ${last_name} has been deactivated`);
                }
            }, () => {
                // Revert back model state
                model.set({
                    active: !nextActiveState
                });
                toast.error("There was an error requesting user change.");
            }).finally(() => {
                destroyPopovers();
            });
        }
    },
    
    mimicUser({ model: user }) {
        store.dispatch(DO_MIMIC_USER(user.id));
    },
    
    resetPassword(childView) {
        sweetalert({
            title: strings.confirmResetPasswordUser,
            content: $(`<blockquote class='remove-user'>${userTemplate(childView.model.toJstJSON())}</blockquote>`)[0],
            icon: "warning",
            buttons: {
                confirm: {
                    text: strings.login.resetPassword,
                    className: "btn-confirm"
                },
                cancel: {
                    visible: true
                }
            }
        }).then((confirmed) => {
            if (confirmed) {
                const username = childView.model.get("username");
                const showError = () => {
                    toast.error(strings.login.errors.resetError);
                };
                
                if (!username) {
                    showError();
                    throw new Error("Unable to reset user's password. No email.");
                }
                
                return request(authentication.password.reset, {
                    data: {
                        email: childView.model.get("username")
                    },
                    ensureAuth: false,
                    method: "POST"
                }).then(() => {
                    const user = childView.model.toJSON();
                    toast.success(`Password is reset for ${user.first_name} ${user.last_name}. Email sent successfully.`);
                }).catch(() => showError());
            }
        });
    },
    
    sendActivation(childView) {
        sweetalert({
            title: strings.confirmResendActivationEmail,
            content: $(`<blockquote class='remove-user'>${userTemplate(childView.model.toJstJSON())}</blockquote>`)[0],
            icon: "warning",
            buttons: {
                confirm: {
                    text: strings.send,
                    className: "btn-confirm"
                },
                cancel: {
                    visible: true
                }
            }
        }).then((confirmed) => {
            if (confirmed) {
                const username = childView.model.get("username");
                const showError = () => {
                    toast.error(strings.login.errors.activationEmailError);
                };
                
                if (!username) {
                    showError();
                    throw new Error("Unable to request new activation email. No email.");
                }
                
                return request(authentication.sendActivationEmail, {
                    data: {
                        email: childView.model.get("username")
                    },
                    method: "POST"
                }).then(() => {
                    const user = childView.model.toJSON();
                    toast.success(`Activation email with instructions to ${user.first_name} ${user.last_name} sent successfully.`);
                }).catch(() => showError());
            }
        });
    },
    
    showPermissionError() {
        this.showChildView("list", new View({
            template: () => `<div class="pz-alert alert alert-danger clearfix" role="alert">
                <div class="triangle-icon"><i class="far fa-exclamation-triangle"></i></div>
                <div class="alert-message">{{ strings.noCreateEditUsersPermissions }}</div>
            </div>`
        }));
    },
    
    addNewUser() {
        this.createOrEditUser(new UserModel());
    },
    
    isCurrentActiveUser(model) {
        const activeUser = Radio.channel("main").request("get:user");
        return activeUser.id === model.id;
    },
    
    editExistingUser(rowView) {
        const { model } = rowView;
        
        if (this.isCurrentActiveUser(model)) {
            return this.showThatsYou(model);
        }
        
        return this.createOrEditUser(model);
    },
    
    filterRows: () => true,
    
    noDataMessage: strings.noUsersForMerchant,
    
    makeList() {
        this.tableModel = new SmartTableModel({
            mode: _.result(this, "tableMode"),
            pager: true,
            fixed: true,
            filterRows: this.filterRows.bind(this),
            noDataMessage: _.result(this, "noDataMessage"),
            afterRender(collection, children) {
                children.each((child) => {
                    if (child.model.isDeactivated()) {
                        child.$el.addClass("deactivated");
                    }
                });
            },
            columns: [
                {
                    name: "User",
                    cellClassName: "regular-user clearfix",
                    attribute: "first_name",
                    beforeRender: (content, rowModelData, user, column) => {
                        if (!user) { return; }
                        const bkg = `background-color: ${user.getUserColor()}`;
                        const [letter = "P"] = user.getDisplayName();
    
                        return `<div class="profile-photo pull-left" style="${bkg}">${letter}</div>
                                <div class="user-data">${userTemplate(user.toJstJSON())}</div>`;
                    },
                    sortable: true,
                    cell: "text"
                },
                {
                    name: "Merchant",
                    cellClassName: "merchant_ref",
                    attribute: "merchant_ref",
                    beforeRender: (content, rowModelData, user, column) => references.toAlias(content, "merchant"),
                    sortable: false,
                    cell: "text"
                },
                {
                    name: "Email address",
                    cellClassName: "username",
                    attribute: "username",
                    beforeRender: (content, rowModelData, user, column) => {
                        const username = `<span style="vertical-align: middle;">${_.escape(user.get("username"))}</span>`;
                        
                        const unlockIcon = user.isLocked() ? `<div class="icon unlock-user" data-toggle="tooltip" title="${strings.unlockUser}"><i class="far fa-lock"/></div>` : "";
                        const resetPassIcon = user.isActive() ? `<div class="icon reset-pass" data-toggle="tooltip" title="${strings.login.resetPassword}"><span class="fa-layers fa-fw" aria-hidden="true">
                                                                      <i class="far fa-undo-alt"></i>
                                                                      <i class="far fa-lock" data-fa-transform="shrink-8"></i>
                                                                    </span></div>` : "";
                        const resendActivationEmailIcon = user.hasNeverLoggedIn() || user.isSsoUser() ? `<div class="icon send-activation" data-toggle="tooltip" title="${strings.resendActivationEmail}"><i class="far fa-envelope send-activation" aria-hidden="true"></i></div>` : "";
                        const toolbar = `<div class="user-toolbar">${unlockIcon}${resendActivationEmailIcon || resetPassIcon}</div>`;
                        
                        return `<div class="username-wrap">${username}${toolbar}</div>`;
                    },
                    cell: "text",
                    sortable: true
                },
                {
                    name: "Last signed in",
                    cellClassName: "last-sign-in",
                    attribute: "last_login",
                    cell: "date_time",
                    cellOptions: {
                        displayLocal: true,
                        format: "MMMM Do YYYY, h:mm a",
                        noDateMessage: "User has never logged in"
                    },
                    sortable: true
                },
                {
                    name: "Activate user",
                    attribute: "toggleUserActivation",
                    cell: "link",
                    cellClassName: "icon",
                    cellOptions: {
                        icon: (model) => {
                            if (model && model.isDeactivated()) {
                                return "fal fa-square";
                            }
                            return "fal fa-check-square";
                        }
                    },
                    tooltip(model) {
                        return model.isDeactivated() ? strings.reactivateUser : strings.deactivateUser;
                    },
                    width: 65
                },
                {
                    name: "",
                    attribute: "mimicUser",
                    cell: "link",
                    cellClassName: (model) => {
                        if (model && model.get("active") === false) {
                            return "hide-icon";
                        }
                        return "icon mimic-user-start";
                    },
                    cellOptions: {
                        icon: "fal fa-user-secret mimic-user-icon"
                    },
                    tooltip: "Mimic user",
                    width: 65
                }
            ].map(this.columnsMapper).filter(Boolean)
        });
        
        this.showChildView("list", new UsersTable({
            model: this.tableModel,
            collection: this.users
        }));
    },
    
    onRender() {
        this.renderView();
    }
};
